/**
 * Shared auth-success applier (T27.2.10.1 Phase B).
 *
 * The verify + reset screens issue a session exactly like login, so they reuse
 * this single applier rather than each re-implementing the LoginPage path:
 * persist the access token, persist the native-only refresh token into the
 * Keystore, then navigate to home. Mirrors LoginPage's `applyAuthSuccess` (do
 * not fork the session-persistence logic — T27.2.2.1 / T27.2.10.1).
 *
 * This is the post-verify/post-reset path: there is no invite to surface (those
 * flows don't carry an invite token), so it always lands on home.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '@shopista/shared/types';
import type { WebTokenStorage } from '../../auth';
import { routes } from '../../routes/paths';

/**
 * The subset of an auth response this applier needs. T48: `/verify` can ack-only
 * (an already-authenticated caller — no session), so `sessionToken`/`user` are
 * optional; this applies the session only when both are present, and always
 * navigates home. Register / login / reset always carry them.
 */
export interface AuthSuccessLike {
  sessionToken?: string | undefined;
  user?: User | undefined;
  refreshToken?: string | undefined;
}

export function useApplyAuthSuccess(tokens: WebTokenStorage): (resp: AuthSuccessLike) => void {
  const navigate = useNavigate();
  return useCallback(
    (resp: AuthSuccessLike): void => {
      // A session was issued (register / login / reset / unauthenticated verify):
      // persist it. An ack-only response (authenticated verify) skips this and
      // just lands home — the caller is already signed in.
      if (resp.sessionToken && resp.user) {
        tokens.setSession({
          accessToken: resp.sessionToken,
          user: resp.user,
        });
        // T27.2.2.2 — persist the native-only refresh token (web omits it).
        if (resp.refreshToken && tokens.setRefreshToken) {
          tokens.setRefreshToken(resp.refreshToken);
        }
      }
      navigate(routes.home, { replace: true });
    },
    // navigate + tokens are stable identities; no reactive inputs.
    // The exhaustive-deps rule is not registered in this project.
    [],
  );
}
